/*
 * Copyright 2026 Sony Corporation
 */
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

const BLUE = '#1976d2';
const GREY = '#5f6b7a';

const Node = ({
  x,
  y,
  w,
  h,
  title,
  note,
  fill = '#fff',
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  note?: string;
  fill?: string;
}) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx={8} fill={fill} stroke={BLUE} strokeWidth={1.5} />
    <text x={x + w / 2} y={y + (note ? h / 2 - 3 : h / 2 + 5)} textAnchor="middle" fontSize={14} fontWeight={600}>
      {title}
    </text>
    {note && (
      <text x={x + w / 2} y={y + h / 2 + 14} textAnchor="middle" fontSize={11} fill={GREY}>
        {note}
      </text>
    )}
  </g>
);

const Arrow = ({
  d,
  label,
  lx,
  ly,
  anchor = 'middle',
}: {
  d: string;
  label: string;
  lx: number;
  ly: number;
  anchor?: 'start' | 'middle' | 'end';
}) => (
  <g>
    <path d={d} fill="none" stroke={BLUE} strokeWidth={1.5} markerStart="url(#rt-arrow)" markerEnd="url(#rt-arrow)" />
    <text x={lx} y={ly} fontSize={11} fill={GREY} textAnchor={anchor}>
      {label}
    </text>
  </g>
);

/** How the parts fit together: the cloud builds the app, the agent on the PC runs it, the app drives the camera. */
export const RuntimeDiagram = () => (
  <Box
    component="svg"
    viewBox="0 0 560 300"
    role="img"
    aria-label="Architecture: the cloud service sends apps to the agent on your PC, which runs the app that controls the camera"
    sx={{ width: '100%', maxWidth: 560, height: 'auto', fontFamily: 'inherit' }}
  >
    <defs>
      <marker
        id="rt-arrow"
        viewBox="0 0 10 10"
        refX="5"
        refY="5"
        markerWidth="7"
        markerHeight="7"
        orient="auto-start-reverse"
      >
        <path d="M0 0 10 5 0 10Z" fill={BLUE} />
      </marker>
    </defs>
    {/* The cloud, right above the agent that talks to it. */}
    <path
      d="M392 90A24 24 0 0 1 392 44A34 34 0 0 1 452 24A31 31 0 0 1 512 38A28 28 0 0 1 528 90Z"
      fill="#e8f1fc"
      stroke={BLUE}
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
    <text x={460} y={64} textAnchor="middle" fontSize={14} fontWeight={600}>
      Cloud service
    </text>
    <text x={460} y={80} textAnchor="middle" fontSize={11} fill={GREY}>
      builds your app
    </text>

    {/* The network the camera and the PC share. */}
    <rect x={8} y={130} width={544} height={162} rx={20} fill="none" stroke={BLUE} strokeDasharray="6 4" />
    <text x={24} y={152} fontSize={12} fill={GREY}>
      Your network (Wi-Fi / LAN)
    </text>

    <Node x={30} y={185} w={130} h={70} title="Camera" note="Sony Alpha" />

    <rect x={290} y={165} width={240} height={112} rx={10} fill="#f5f8fc" stroke={BLUE} strokeWidth={1.5} />
    <text x={305} y={185} fontSize={13} fontWeight={600}>
      Local PC (runtime)
    </text>
    <Node x={305} y={196} w={100} h={66} title="App" note="your app" />
    <Node x={415} y={196} w={100} h={66} title="Agent" note="system tray" />

    <Arrow d="M465 194 V96" label="apps · status" lx={458} ly={122} anchor="end" />
    <Arrow d="M300 229 H166" label="PTP/IP" lx={233} ly={222} />
    <path d="M413 229 H409" stroke={BLUE} strokeWidth={1.5} markerEnd="url(#rt-arrow)" />
  </Box>
);

/** A command or file name as the person types or sees it. */
const Code = ({ children }: { children: ReactNode }) => (
  <Box
    component="code"
    sx={{
      fontFamily: 'monospace',
      fontSize: '0.85em',
      px: 0.75,
      py: 0.25,
      borderRadius: 1,
      bgcolor: 'action.selected',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

const STEPS: { title: string; text: ReactNode }[] = [
  {
    title: 'Create a runtime',
    text: (
      <>
        Click <b>+ New runtime</b>, then choose the PC’s <b>platform</b> and the <b>camera models</b> it works with.
      </>
    ),
  },
  {
    title: 'Download the agent',
    text: (
      <>
        Click <b>Download agent</b> on the runtime’s card. It is made for that platform and includes the runtime’s{' '}
        <b>credentials</b>.
      </>
    ),
  },
  {
    title: 'Install it on the PC',
    text: (
      <>
        Unzip it and run <Code>install.cmd</Code> on Windows or <Code>./install.sh</Code> on Linux. The agent starts in
        the <b>system tray</b>.
      </>
    ),
  },
  {
    title: 'Build an app',
    text: (
      <>
        When the card shows{' '}
        <Chip size="small" color="success" label="online" component="span" sx={{ height: 20, fontSize: 12 }} />, open{' '}
        <b>Projects</b> and create a project for this runtime.
      </>
    ),
  },
];

/** The picture of the architecture with the steps a person follows to set a runtime up. */
export const RuntimeGuide = () => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: { md: 'center' } }}>
      <Box sx={{ flex: '1 1 55%', minWidth: 0 }}>
        <RuntimeDiagram />
      </Box>
      <Box sx={{ flex: '1 1 45%' }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Set up a runtime
        </Typography>
        <Stack component="ol" spacing={1.75} sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {STEPS.map((step, index) => (
            <Stack component="li" key={step.title} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
              <Box
                aria-hidden
                sx={{
                  flexShrink: 0,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {index + 1}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.6 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ '& b': { color: 'text.primary' } }}>
                  {step.text}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  </Paper>
);
